import { Trans, t } from '@lingui/macro';
import { X, Trash2, RefreshCw, Loader2, Circle } from "lucide-react";
import { useEffect, useState } from "react";
import { PagecallParticipant } from "../../../models/DTO";
import { RoomItem, getPCRoomId } from "../../../models/RoomItem";
import { Fire } from "../../../services/FireClient";
import { My } from "../../../services/My";
import { UtilApp } from "../../../services/Utils/UtilApp";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { useToast } from "../../../hooks/use-toast";

const PagecallOpenModal: React.FC<{ item: RoomItem, onClose: () => void }> = ({ item, onClose }) => {
  const { toast } = useToast();
  const [participants, setParticipants] = useState<PagecallParticipant[]>([]);
  const [inProgress, setInProgress] = useState(false);
  const [accessToken, setAccessToken] = useState<string>('');

  // Alert state
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);

  useEffect(() => {
    fetchAccessToken();
    fetchParticipants();
  }, []);

  async function fetchAccessToken() {
    try {
      setAccessToken('');
      const r = await Fire.item.getPagecallAccessToken({ user: My.user });
      setAccessToken(r.accessToken);
    } catch (error: any) {
      toast({ title: t`Error`, description: error.message });
    }
  }

  async function fetchParticipants() {
    setParticipants([]);
    const pcRoomId = getPCRoomId(item.url);
    if (pcRoomId) {
      setInProgress(true);
      try {
        const r = await Fire.item.getPagecallRoom({ pcRoomId: pcRoomId });
        setParticipants(r.participants);
      } catch (error: any) {
        toast({ title: t`Error`, description: error.message });
      }
      setInProgress(false);
    }
  }

  return (
    <>
      <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="flex flex-row items-center justify-between border-b pb-2 space-y-0">
            <Button variant="ghost" size="icon" onClick={onClose} className="-ml-2">
              <X className="h-5 w-5" />
            </Button>
            <DialogTitle>{item.title}</DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              disabled={My.user.id != item.user.id}
              onClick={() => setDeleteAlertOpen(true)}
              className="text-red-500 hover:text-red-700 hover:bg-red-50 -mr-2"
            >
              <Trash2 className="h-5 w-5" />
            </Button>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="text-sm text-gray-700">
              <Trans>Meeting room "{item.title}" was created by {item.user.name} {UtilApp.dateStrToTimeAndDay(item.created, My.user.lang)}.</Trans>
            </div>

            <div className="flex justify-center">
              <Button onClick={connect} disabled={!accessToken} className="w-full">
                <Trans>Connect</Trans>
              </Button>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm"><Trans>Participants: {inProgress ? '...' : participants.length}</Trans></span>
                {!inProgress && (
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={fetchParticipants}>
                    <RefreshCw className="h-4 w-4 text-gray-500" />
                  </Button>
                )}
              </div>

              {inProgress && <Loader2 className="h-5 w-5 animate-spin text-gray-400 mx-auto" />}

              {!inProgress && participants.map(p => (
                <div key={p.id} className="flex items-center gap-2 text-sm p-2 rounded-md hover:bg-gray-50">
                  <Circle className={`h-2 w-2 ${p.online ? 'fill-green-500 text-green-500' : 'fill-gray-300 text-gray-300'}`} />
                  <span>{p.name}</span>
                </div>
              ))}

              {!inProgress && participants.length === 0 && (
                <div className="text-sm text-gray-500 p-2"><Trans>No participants yet.</Trans></div>
              )}
            </div>
          </div>

          <div className="bg-gray-50 -mx-6 -mb-6 p-4 text-xs text-gray-500 border-t mt-2 rounded-b-lg">
            <Trans>Meetings are facilitated by Pagecall, a communication software with real-time audio, video, and whiteboard interactions.</Trans>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteAlertOpen} onOpenChange={setDeleteAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle><Trans>Delete meeting room</Trans></AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>Are you sure you want to delete this meeting room?</Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              <Trans>Delete</Trans>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  function connect() {
    window.open(item.url + `&access_token=${accessToken}`, '_blank');
  }

  async function handleDelete() {
    const pcRoomId = getPCRoomId(item.url);
    try {
      await Fire.item.delete(item);
      Fire.item.deletePagecallRoom({ pcRoomId: pcRoomId }).catch(e => toast({ title: t`Error`, description: e.message }));
      onClose();
    } catch (e: any) {
      toast({ title: t`Error`, description: e.message });
    }
  }
}

export default PagecallOpenModal;
